<template>
  <div v-if="content" class="whitespace-pre-wrap leading-none" v-html="sanitizedHtml" />
</template>

<script lang="ts" setup>
import sanitizeHtml from 'sanitize-html'

const customClassTransformationGenerator =
  (classes: string) =>
  (tagName: string, attribs: any): any => {
    return {
      tagName: tagName,
      attribs: {
        ...attribs,
        class: classes
      }
    }
  }

const props = defineProps<{
  content: string
}>()

const sanitizedHtml = computed(() => {
  return sanitizeHtml(props.content, {
    allowedTags: [
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'blockquote',
      'p',
      'a',
      'ul',
      'ol',
      'nl',
      'li',
      'b',
      'i',
      'strong',
      'em',
      'strike',
      's',
      'code',
      'hr',
      'br',
      'div',
      'table',
      'thead',
      'caption',
      'tbody',
      'tr',
      'th',
      'td',
      'pre',
      'img',
      'del'
    ],

    allowedSchemes: ['http', 'https', 'data'],

    allowedAttributes: {
      a: ['href', 'target'],
      img: ['src', 'alt']
    },

    allowedClasses: {
      '*': ['*']
    },

    transformTags: {
      // always add target=_blank
      a: (tagName: string, attribs: any): any => {
        return {
          tagName: 'a',
          attribs: {
            href: attribs.href,
            target: '_blank',
            class: 'tw-text-riser-blue hover:tw-underline'
          }
        }
      },

      h1: customClassTransformationGenerator(
        'tw-text-xl tw-font-semibold tw-uppercase lg:tw-text-2xl'
      ),
      h2: customClassTransformationGenerator(
        'tw-text-lg tw-font-semibold tw-uppercase lg:tw-text-xl'
      ),
      h3: customClassTransformationGenerator('tw-font-semibold tw-uppercase lg:tw-text-lg'),
      h4: customClassTransformationGenerator('tw-font-semibold tw-uppercase lg:tw-text-lg'),
      h5: customClassTransformationGenerator('tw-font-semibold tw-uppercase'),
      h6: customClassTransformationGenerator('tw-font-semibold tw-uppercase'),

      ul: customClassTransformationGenerator('tw-list-disc tw-list-inside'),
      ol: customClassTransformationGenerator('tw-list-decimal tw-list-inside'),
      table: customClassTransformationGenerator('tw-w-full'),
      tr: customClassTransformationGenerator('tw-pb-3 tw-border-b'),
      td: customClassTransformationGenerator('tw-text-center'),
      blockquote: customClassTransformationGenerator('tw-border-l-4 tw-pl-4 tw-text-gray-600'),
      del: customClassTransformationGenerator('tw-line-through')
    }
  })
})
</script>
